import { BenchmarkDiagramPoint, BenchmarkDiagramSeries } from "../shared/benchmarks/benchmarks-model";
import { Card } from "../shared/project/cards/card";
import { CardsModel, Tab } from "../shared/project/cards/cards-model";
import { RoundingHelper, RoundingSettings } from "../shared/rounding/rounding-helper";

export class CardHelper {
    public static calcCardsFancyNumContexts(cards: Card[]) {
        for (let i = 0; i < cards.length; ++i) {
            cards[i].outcomeCtx = this.createFancyNumCtxForNum((cards[i].altViewSeq.outcomeOldVal + cards[i].altViewSeq.outcomeNewVal) / 2);
            cards[i].formCompCtx = this.createFancyNumCtxForNum((cards[i].altViewSeq.formCompOldVal + cards[i].altViewSeq.formCompOldVal) / 2);
    
            let nums = [ cards[i].altViewSeq.formCompOldVal, cards[i].altViewSeq.formCompNewVal ];
            if (cards[i].selectedDiagram) {
                nums.push(cards[i].selectedDiagram.maxVal);
            }
            let previewDiagramRoundingSettings = RoundingHelper.CalcRoundingSettingsForDiagram(nums);
            cards[i].diagramMinMaxCtx = {
                roundTo: previewDiagramRoundingSettings.roundTo,
                scale: previewDiagramRoundingSettings.scale,
                use2stepRounding: false,
            };
            cards[i].diagramValsCtx = {
                roundTo: previewDiagramRoundingSettings.roundTo,
                scale: previewDiagramRoundingSettings.scale,
                use2stepRounding: true,
            };
        }
    }

    public static calcOutcomeFancyNumContext(cards: Card[], otherNums: number[]) {
        let minVal = Math.min.apply(null, otherNums.filter(n => n !== 0).map(n => Math.abs(n)));
        const eps = 0.0000000000001;
        for (let i = 0; i < cards.length; ++i) {
            if (Math.abs(cards[i].seqDriverValue) < minVal && Math.abs(cards[i].seqDriverValue) > eps) {
                minVal = Math.abs(cards[i].seqDriverValue);
            }
            if (Math.abs(cards[i].absDriverValue) < minVal && Math.abs(cards[i].absDriverValue) > eps) {
                minVal = Math.abs(cards[i].absDriverValue);
            }
        }
        return CardHelper.createFancyNumCtxForNum(minVal);
    }

    private static createFancyNumCtxForNum(num: number) {
        let roundingSettings = RoundingHelper.CalcRoundingSettingsForNum(num);
        return {
            roundTo: roundingSettings.roundTo,
            scale: roundingSettings.scale,
            use2stepRounding: true,
        };
    }

    public static initCardDiagram(card: Card, selectedDiagramSeriesName: string, oldControlVal: string, newControlVal: string, seqMode: boolean, viewBoxWidth:number, viewBoxHeight: number): void {
        card.selectedDiagram = (seqMode ? card.altViewSeq.diagramSeries : card.altViewSeq.diagramSeries).find(s => s.name === selectedDiagramSeriesName);

        let self = this;
        function initSeries(series: BenchmarkDiagramSeries[]) {
            let roundingSettings = self.CalcRoundingSettingsForDiagram(series);
            let oldControlValPt: BenchmarkDiagramPoint;
            let newControlValPt: BenchmarkDiagramPoint;
            for (let k = 0; k < series.length; ++k) {
                for (let m = 0; m < series[k].points.length; ++m) {
                    if (series[k].points[m].controlValue == oldControlVal) {
                        oldControlValPt = series[k].points[m];
                    }
                    if (series[k].points[m].controlValue == newControlVal) {
                        newControlValPt = series[k].points[m];
                    }
                }
            }
            for (let k = 0; k < series.length; ++k) {
                self.initDiagram(series[k], roundingSettings, oldControlValPt, newControlValPt, viewBoxWidth, viewBoxHeight);
            }
        }
        initSeries(card.altViewSeq.diagramSeries);
        if (card.altViewTot && card.altViewTot.diagramSeries) {
            initSeries(card.altViewTot.diagramSeries);
        }
    }

    private static CalcRoundingSettingsForDiagram(diagramSeries: BenchmarkDiagramSeries[]): RoundingSettings {
        let allNums: number[] = [];
        for (let i = 0; i < diagramSeries.length; ++i) {
            for (let j = 0; j < diagramSeries[i].points.length; ++j) {
                if (diagramSeries[i].points[j]) {
                    allNums.push(diagramSeries[i].points[j].value);
                }
            }
        }
        return RoundingHelper.CalcRoundingSettingsForDiagram(allNums);
    }
    
    private static initDiagram(diagramSeries: BenchmarkDiagramSeries, roundingSettings: RoundingSettings, oldControlValPoint: BenchmarkDiagramPoint, newControlValPoint: BenchmarkDiagramPoint, viewBoxWidth:number, viewBoxHeight: number) {
        if (!diagramSeries.points.length) {
            return;
        }

        let maxXPos = 0;
        let minXPos = 99;
        let xPoses =  diagramSeries.points.filter(p => p).map(p => p.xPos);
        if (oldControlValPoint) {
            xPoses.push(oldControlValPoint.xPos);
        }
        if (newControlValPoint) {
            xPoses.push(newControlValPoint.xPos);
        }
        for (let j = 0; j < xPoses.length; ++j) {
            let curXPos = xPoses[j];
            if (curXPos > maxXPos) {
                maxXPos = curXPos;
            }
            if (curXPos < minXPos) {
                minXPos = curXPos;
            }
        }
        let width = maxXPos - minXPos + 1;
        
        let minVal = roundingSettings.minAxis;
        let maxVal = roundingSettings.maxAxis;
        let xStep = viewBoxWidth / (width + 2);
        let verticalGap = 0;
        let yStep = (viewBoxHeight - 2 * verticalGap) / (maxVal - minVal);
        
        function convertX(x: number) {
            return xStep * x;
        }
        let self = this;
        function convertY(y: number) {
            return viewBoxHeight - verticalGap - yStep * (y - minVal);
        }

        for (let j = 0; j < diagramSeries.points.length; ++j) {
            let curPoint = diagramSeries.points[j];
            if (!curPoint) {
                continue;
            }
            curPoint.x = xStep * (curPoint.xPos - minXPos + 1.5);
            curPoint.y = viewBoxHeight - verticalGap - yStep * (curPoint.value - minVal);
        }        
        diagramSeries.minVal = roundingSettings.roundTo * Math.floor((minVal - verticalGap / yStep) / roundingSettings.roundTo);
        diagramSeries.maxVal = roundingSettings.roundTo * Math.ceil((maxVal + verticalGap / yStep) / roundingSettings.roundTo);

        if (oldControlValPoint) {
            diagramSeries.oldControlValX = convertX(oldControlValPoint.xPos - minXPos + 1.5);
            diagramSeries.oldControlValY = convertY(oldControlValPoint.value);
        }
        if (newControlValPoint) {
            diagramSeries.newControlValX = convertX(newControlValPoint.xPos - minXPos + 1.5);
            diagramSeries.newControlValY = convertY(newControlValPoint.value);
        }

        if (diagramSeries.bandTetragon && !diagramSeries.bandTetragonStr) {
            let tetragon = diagramSeries.bandTetragon;
            diagramSeries.bandTetragonStr = `${convertX(tetragon.leftX+1.5)},${convertY(tetragon.topLeftY)} ${convertX(tetragon.rightX+1.5)},${convertY(tetragon.topRightY)} ${convertX(tetragon.rightX+1.5)},${convertY(tetragon.bottomRightY)} ${convertX(tetragon.leftX+1.5)},${convertY(tetragon.bottomLeftY)}`;
        }
    }

    public static findByTabUId(views: CardsModel[], tabUId: string): [CardsModel, Tab | undefined] {
        for (let i=0; i < views.length; ++i) {
            if (views[i].tabUId == tabUId) {
                return [ views[i], undefined ];
            }
            if (views[i].tabs) {
                for (let j=0; j < views[i].tabs.length; ++j) {
                    if (views[i].tabs[j].tabUId == tabUId) {
                        return [ views[i], views[i].tabs[j] ];
                    }
                }
            }
        }
        return [undefined, undefined ];
    }
}